/**
 * HPN NAD3 Quiz Funnel v4 — Engine
 * State machine: welcome -> gender -> goals -> stimulants -> age -> break1
 *   -> q5 (adaptive) -> prior -> break2 -> q7 (adaptive) -> identity
 *   -> lead capture -> result
 *
 * Scoring (see quiz-nad3-content.js): Q2 (exactly 2 goals) + Q3 (stimulants)
 * point totals decide NAD3 vs NAD3 Extra Strength. Q4 age decides the Break 1
 * age band. The goal pair decides bridge/Q5/Q7/result copy. Q6 decides the
 * Break 2 variant. None of this is content — it is pure routing logic.
 */
(function () {
  'use strict';

  var STATE_VERSION = 2;

  function Nad3Quiz(root) {
    if (!root || !window.HPNQuizContent || root.getAttribute('data-quiz-initialized') === 'true') return null;
    root.setAttribute('data-quiz-initialized', 'true');

    var C = window.HPNQuizContent;
    var sectionId = root.getAttribute('data-section-id') || 'default';
    var storageKey = 'hpn_nad3_quiz_state_' + sectionId;

    var stepEls = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-step]'), function (el) {
      stepEls[el.getAttribute('data-step')] = el;
    });

    var break1ImageMap = {};
    var break1ImageMapEl = root.querySelector('[data-break1-image-map]');
    if (break1ImageMapEl) {
      try { break1ImageMap = JSON.parse(break1ImageMapEl.textContent); } catch (e) {}
    }

    var STEP_ORDER = ['welcome', 'gender', 'goals', 'stimulants', 'age', 'break1', 'q5', 'prior', 'break2', 'q7', 'identity', 'lead', 'result'];
    var QUESTION_STEPS = ['gender', 'goals', 'stimulants', 'age', 'q5', 'prior', 'q7', 'identity'];
    var stepsPresent = STEP_ORDER.filter(function (key) { return !!stepEls[key]; });
    if (!stepsPresent.length) return null;

    function createInitialState() {
      return {
        stepIndex: 0,
        answers: {},
        pairKey: null,
        sku: null,
        ageBand: null,
        break2Variant: null,
        startedAt: null,
        completedAt: null
      };
    }

    var state = createInitialState();

    function loadState() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        var parsed = JSON.parse(raw);
        if (parsed && parsed.version === STATE_VERSION && parsed.state && typeof parsed.state === 'object') {
          state = Object.assign(createInitialState(), parsed.state);
          state.answers = state.answers && typeof state.answers === 'object' ? state.answers : {};
          if (!Number.isInteger(state.stepIndex) || state.stepIndex < 0 || state.stepIndex >= stepsPresent.length) {
            state.stepIndex = 0;
          }
          if (state.answers.gender && state.answers.gender !== 'man' && state.answers.gender !== 'woman') {
            delete state.answers.gender;
            state.completedAt = null;
            state.stepIndex = stepsPresent.indexOf('gender');
          }
        }
      } catch (e) { /* ignore corrupt state */ }
    }

    function saveState() {
      try {
        var persistedState = JSON.parse(JSON.stringify(state));
        delete persistedState.answers.firstName;
        delete persistedState.answers.email;
        delete persistedState.answers.phone;
        delete persistedState.answers.smsConsentText;
        delete persistedState.answers.smsConsentedAt;
        sessionStorage.setItem(storageKey, JSON.stringify({ version: STATE_VERSION, state: persistedState }));
      } catch (e) { /* storage unavailable */ }
    }

    function clearState() {
      try { sessionStorage.removeItem(storageKey); } catch (e) {}
    }

    function emit(name, detail) {
      root.dispatchEvent(new CustomEvent('nad3-quiz:' + name, { bubbles: true, detail: detail || {} }));
    }

    function markStarted() {
      if (state.startedAt) return;
      state.startedAt = new Date().toISOString();
      saveState();
      emit('started', { startedAt: state.startedAt });
    }

    // -----------------------------------------------------------------
    // NAVIGATION
    // -----------------------------------------------------------------
    function currentStepKey() { return stepsPresent[state.stepIndex]; }

    function prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function showStep(index) {
      stepsPresent.forEach(function (key, i) {
        var el = stepEls[key];
        if (!el) return;
        el.classList.toggle('nad3-step--active', i === index);
        el.hidden = i !== index;
      });
      state.stepIndex = index;
      root.setAttribute('data-current-step', stepsPresent[index] || 'welcome');
      emit('step-viewed', { step: stepsPresent[index], index: index, ageBand: state.ageBand, break2Variant: state.break2Variant, pairKey: state.pairKey });
      var resultBundle = document.querySelector('[data-nad3-result-bundle]');
      if (resultBundle) resultBundle.hidden = stepsPresent[index] !== 'result';
      saveState();
      updateProgress();
      var backButton = root.querySelector('[data-quiz-back]');
      if (backButton) backButton.hidden = index === 0 || stepsPresent[index] === 'result';
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      var stepEl = stepEls[stepsPresent[index]];
      var heading = stepEl && stepEl.querySelector('[data-result-sku]:not([hidden]) [data-step-heading]');
      if (!heading && stepEl) heading = stepEl.querySelector('[data-step-heading]');
      if (heading) setTimeout(function () { heading.focus({ preventScroll: true }); }, prefersReducedMotion() ? 0 : 150);
    }

    // Picking an answer moves on by itself after a beat; Continue stays visible for
    // people who prefer it. Only single-answer questions call this; multi-select steps, age, breaks and the lead form wait for Continue.
    var advanceTimer = null;
    function scheduleAdvance(stepKey, continueBtn) {
      clearTimeout(advanceTimer);
      advanceTimer = setTimeout(function () {
        if (currentStepKey() === stepKey && !continueBtn.disabled) continueBtn.click();
      }, prefersReducedMotion() ? 0 : 350);
    }

    function goNext() {
      var key = currentStepKey();
      renderStepIfNeeded(stepsPresent[state.stepIndex + 1]);
      if (state.stepIndex + 1 < stepsPresent.length) {
        showStep(state.stepIndex + 1);
        var questionIndex = QUESTION_STEPS.indexOf(key);
        if (questionIndex !== -1) {
          emit('step-answered', { step: key, questionNumber: questionIndex + 1, answers: Object.assign({}, state.answers) });
        }
      }
    }

    function goPrev() {
      if (state.stepIndex > 0) showStep(state.stepIndex - 1);
    }

    function updateProgress() {
      var progressRoot = root.querySelector('[data-progress-root]');
      var progressBar = root.querySelector('[data-progress-bar]');
      var idx = QUESTION_STEPS.indexOf(currentStepKey());
      var isBreak = currentStepKey() === 'break1' || currentStepKey() === 'break2';
      if (currentStepKey() === 'break1') idx = QUESTION_STEPS.indexOf('age');
      if (currentStepKey() === 'break2') idx = QUESTION_STEPS.indexOf('prior');
      if (progressRoot) progressRoot.hidden = idx < 0 || isBreak;
      if (idx < 0) return;
      var pct = Math.round(((idx + 1) / QUESTION_STEPS.length) * 100);
      Array.prototype.forEach.call(root.querySelectorAll('[data-progress-fill]'), function (fill) { fill.style.width = pct + '%'; });
      Array.prototype.forEach.call(root.querySelectorAll('[data-progress-label]'), function (label) {
        label.textContent = (idx + 1) + ' of ' + QUESTION_STEPS.length;
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-progress-percent]'), function (label) {
        label.textContent = pct + '% Complete';
      });
      if (progressBar) progressBar.setAttribute('aria-valuenow', String(idx + 1));
    }

    // -----------------------------------------------------------------
    // SCORING / ROUTING
    // -----------------------------------------------------------------
    function computeSku() {
      return C.scoreSku(state.answers.goals || [], state.answers.stimulant);
    }

    // -----------------------------------------------------------------
    // GENERIC OPTION RENDERING (single + multi select)
    // -----------------------------------------------------------------
    function renderOptions(container, options, opts) {
      opts = opts || {};
      container.innerHTML = '';
      container.setAttribute('data-multi-select', opts.multi ? 'true' : 'false');
      options.forEach(function (option, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'nad3-choice-btn';
        btn.setAttribute('data-choice-key', option.key || String(i));
        btn.setAttribute('aria-pressed', 'false');
        if (option.exclusive) btn.setAttribute('data-exclusive', 'true');
        var label = document.createElement('span');
        label.className = 'nad3-choice-btn__label';
        label.textContent = option.label;
        var indicator = document.createElement('span');
        indicator.className = 'nad3-choice-btn__indicator';
        indicator.setAttribute('aria-hidden', 'true');
        btn.appendChild(label);
        btn.appendChild(indicator);
        container.appendChild(btn);
      });
    }

    function selectedKeys(container) {
      return Array.prototype.map.call(container.querySelectorAll('.nad3-choice-btn--selected'), function (b) {
        return b.getAttribute('data-choice-key');
      });
    }

    function setSelected(btn, selected) {
      btn.classList.toggle('nad3-choice-btn--selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    function restoreSelections(stepKey, answerField) {
      var el = stepEls[stepKey];
      if (!el) return;
      var container = el.querySelector('[data-options-root]');
      if (!container) return;
      var stored = state.answers[answerField];
      var values = Array.isArray(stored) ? stored : (stored ? [stored] : []);
      Array.prototype.forEach.call(container.querySelectorAll('.nad3-choice-btn'), function (btn) {
        setSelected(btn, values.indexOf(btn.getAttribute('data-choice-key')) !== -1);
      });
    }

    function handleSingleChoice(container, btn, onDone) {
      Array.prototype.forEach.call(container.querySelectorAll('.nad3-choice-btn'), function (b) {
        setSelected(b, false);
      });
      setSelected(btn, true);
      onDone(btn.getAttribute('data-choice-key'));
    }

    function handleMultiChoice(container, btn, opts) {
      opts = opts || {};
      var isExclusive = btn.getAttribute('data-exclusive') === 'true';
      var willSelect = !btn.classList.contains('nad3-choice-btn--selected');

      if (willSelect && isExclusive) {
        // Selecting an EXCLUSIVE option clears every other selection.
        Array.prototype.forEach.call(container.querySelectorAll('.nad3-choice-btn'), function (b) {
          setSelected(b, false);
        });
        setSelected(btn, true);
      } else if (willSelect && !isExclusive) {
        // Selecting a normal option clears any EXCLUSIVE selection.
        Array.prototype.forEach.call(container.querySelectorAll('[data-exclusive="true"].nad3-choice-btn--selected'), function (b) {
          setSelected(b, false);
        });
        var max = opts.max;
        if (max && selectedKeys(container).length >= max) return; // at cap, ignore
        setSelected(btn, true);
      } else {
        setSelected(btn, false);
      }
    }

    // -----------------------------------------------------------------
    // PER-STEP WIRING
    // -----------------------------------------------------------------
    function bindSingleStep(stepKey, answerField) {
      var el = stepEls[stepKey];
      if (!el) return;
      var container = el.querySelector('[data-options-root]');
      var continueBtn = el.querySelector('[data-quiz-continue]');
      if (!container || !continueBtn) return;
      container.addEventListener('click', function (e) {
        var btn = e.target.closest('.nad3-choice-btn');
        if (!btn) return;
        clearTimeout(advanceTimer);
        handleSingleChoice(container, btn, function (key) {
          state.answers[answerField] = key;
          state.completedAt = null;
          saveState();
          syncContinueState(stepKey, { exact: 1 });
          scheduleAdvance(stepKey, continueBtn);
        });
      });
      continueBtn.addEventListener('click', function () {
        if (selectedKeys(container).length !== 1) return;
        goNext();
      });
    }

    // NOTE: this both (a) wires the Continue button exactly once per step
    // (guarded by data-multi-bound) and (b) (re)wires the options container's
    // delegated click handler. (b) is safe to call repeatedly because the
    // options container itself is a persistent DOM node here (options are
    // static markup for 'goals'), so its listener would otherwise duplicate
    // on repeat calls — guarded the same way. Adaptive steps (q5/q7), whose
    // options are rebuilt at runtime, get a fresh node from renderOptions()
    // instead, which attaches its own single-use listener (see below).
    function bindMultiStep(stepKey, answerField, opts) {
      var el = stepEls[stepKey];
      if (!el) return;
      var container = el.querySelector('[data-options-root]');
      var continueBtn = el.querySelector('[data-quiz-continue]');
      if (!container || container.getAttribute('data-multi-bound') === 'true') return;
      container.setAttribute('data-multi-bound', 'true');

      function refreshContinueState() {
        var keys = selectedKeys(container);
        var ok = true;
        if (opts.exact != null) ok = keys.length === opts.exact;
        else if (opts.min != null) ok = keys.length >= opts.min;
        if (continueBtn) continueBtn.disabled = !ok;
      }

      container.addEventListener('click', function (e) {
        var btn = e.target.closest('.nad3-choice-btn');
        if (!btn) return;
        clearTimeout(advanceTimer);
        handleMultiChoice(container, btn, { max: opts.exact || opts.max });
        refreshContinueState();
      });

      if (continueBtn) {
        continueBtn.addEventListener('click', function () {
          var nextAnswers = selectedKeys(el.querySelector('[data-options-root]'));
          if (answerField === 'goals') {
            var previousPair = ensurePairKey();
            var nextPair = nextAnswers.length === 2 ? C.pairKey(nextAnswers[0], nextAnswers[1]) : null;
            if (previousPair !== nextPair) {
              state.pairKey = nextPair;
              delete state.answers.q5;
              delete state.answers.q7;
            }
          }
          state.answers[answerField] = nextAnswers;
          state.completedAt = null;
          saveState();
          goNext();
        });
      }
      refreshContinueState();
    }

    function syncContinueState(stepKey, opts) {
      var el = stepEls[stepKey];
      if (!el) return;
      var container = el.querySelector('[data-options-root]');
      var continueBtn = el.querySelector('[data-quiz-continue]');
      if (!container || !continueBtn) return;
      var count = selectedKeys(container).length;
      if (opts.exact != null) continueBtn.disabled = count !== opts.exact;
      else if (opts.min != null) continueBtn.disabled = count < opts.min;
    }

    function bindNumberStep() {
      var el = stepEls.age;
      if (!el) return;
      var input = el.querySelector('[data-age-input]');
      var continueBtn = el.querySelector('[data-quiz-continue]');
      var errorEl = el.querySelector('[data-quiz-error]');
      if (!continueBtn) return;

      function submitAge() {
        var val = parseInt(input.value, 10);
        if (!val || val < 13 || val > 110) {
          if (errorEl) { errorEl.hidden = false; errorEl.textContent = 'Please enter an age between 13 and 110.'; }
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        if (errorEl) errorEl.hidden = true;
        input.removeAttribute('aria-invalid');
        state.answers.age = val;
        state.ageBand = C.ageBandKey(val);
        state.completedAt = null;
        saveState();
        goNext();
      }

      continueBtn.addEventListener('click', submitAge);
      Array.prototype.forEach.call(el.querySelectorAll('[data-age-adjust]'), function (button) {
        button.addEventListener('click', function () {
          var direction = Number(button.getAttribute('data-age-adjust')) || 0;
          var current = parseInt(input.value, 10);
          if (!Number.isFinite(current)) current = 43;
          input.value = Math.max(13, Math.min(110, current + direction));
          input.removeAttribute('aria-invalid');
          if (errorEl) errorEl.hidden = true;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });
      input.addEventListener('input', function () {
        input.removeAttribute('aria-invalid');
        if (errorEl) errorEl.hidden = true;
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.isComposing) {
          e.preventDefault();
          submitAge();
        }
      });
    }

    function normalizePhone(value) {
      var raw = (value || '').trim();
      if (!raw) return '';
      var digits = raw.replace(/\D/g, '');
      if (raw.charAt(0) === '+' && digits.length >= 8 && digits.length <= 15) return '+' + digits;
      if (digits.length === 10) return '+1' + digits;
      if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
      return null;
    }

    function bindLeadStep() {
      var el = stepEls.lead;
      if (!el) return;
      var form = el.querySelector('[data-quiz-lead-form]');
      var submitBtn = el.querySelector('[data-quiz-lead-submit]');
      if (!form || !submitBtn) return;
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var nameInput = el.querySelector('[data-field="firstName"]');
        var emailInput = el.querySelector('[data-field="email"]');
        var phoneInput = el.querySelector('[data-field="phone"]');
        var smsInput = el.querySelector('[data-field="smsOptIn"]');
        var errorEl = el.querySelector('[data-quiz-error]');

        var email = emailInput ? emailInput.value.trim() : '';
        var name = nameInput ? nameInput.value.trim() : '';
        var phone = normalizePhone(phoneInput ? phoneInput.value : '');
        var smsOptIn = !!(smsInput && smsInput.checked);
        var emailOk = !!(emailInput && emailInput.validity.valid);
        var phoneOk = !smsOptIn || !!phone;

        [nameInput, emailInput, phoneInput].forEach(function (input) {
          if (input) input.removeAttribute('aria-invalid');
        });

        if (!name || !emailOk || !phoneOk) {
          var invalidInput = !name ? nameInput : (!emailOk ? emailInput : phoneInput);
          var message = !name ? 'Please enter your first name.' : (!emailOk ? 'Please enter a valid email address.' : 'Enter a valid phone number before opting in to text messages.');
          if (errorEl) { errorEl.hidden = false; errorEl.textContent = message; }
          if (invalidInput) {
            invalidInput.setAttribute('aria-invalid', 'true');
            invalidInput.focus();
          }
          return;
        }
        if (errorEl) errorEl.hidden = true;

        state.answers.firstName = name;
        state.answers.email = email;
        state.answers.phone = phone;
        state.answers.smsOptIn = smsOptIn;
        // one checkbox covers text messages and marketing emails
        state.answers.emailOptIn = smsOptIn;
        state.answers.smsConsentedAt = smsOptIn ? new Date().toISOString() : null;
        state.answers.smsConsentText = smsOptIn ? (root.getAttribute('data-sms-consent-text') || '') : null;
        saveState();

        emit('email-captured', { email: email, firstName: name, answers: Object.assign({}, state.answers) });
        if (smsOptIn) {
          emit('email-consent-captured', { email: email, firstName: name, consentedAt: state.answers.smsConsentedAt });
          emit('consent-captured', {
            email: email,
            firstName: name,
            phone: phone,
            consentedAt: state.answers.smsConsentedAt,
            consentText: state.answers.smsConsentText
          });
        }
        goNext();
      });

      form.addEventListener('input', function (event) {
        var target = event.target;
        if (target && target.removeAttribute) target.removeAttribute('aria-invalid');
        var errorEl = el.querySelector('[data-quiz-error]');
        if (errorEl) errorEl.hidden = true;
      });
    }

    // -----------------------------------------------------------------
    // DYNAMIC RENDER: BREAK 1 / Q5 / BREAK 2 / Q7 / RESULT
    // -----------------------------------------------------------------
    function renderStepIfNeeded(stepKey) {
      if (stepKey === 'break1') renderBreak1();
      if (stepKey === 'q5') renderAdaptive('q5', C.Q5);
      if (stepKey === 'break2') renderBreak2();
      if (stepKey === 'q7') renderAdaptive('q7', C.Q7);
      if (stepKey === 'result') renderResult();
    }

    function ensurePairKey() {
      if (state.pairKey) return state.pairKey;
      var goals = state.answers.goals || [];
      if (goals.length === 2) state.pairKey = C.pairKey(goals[0], goals[1]);
      return state.pairKey;
    }

    function renderBreak1() {
      var el = stepEls.break1;
      if (!el) return;
      var age = state.answers.age || '';
      var ageBandKey = age ? C.ageBandKey(Number(age)) : state.ageBand || 'mid';
      var band = C.AGE_BANDS[ageBandKey];
      state.ageBand = ageBandKey;

      var headlineEl = el.querySelector('[data-slot="band-headline"]');
      var bodyEl = el.querySelector('[data-slot="band-body"]');
      var bridgeEl = el.querySelector('[data-slot="bridge-body"]');
      var bridgeHeadlineEl = el.querySelector('[data-slot="bridge-headline"]');
      var eyebrowEl = el.querySelector('[data-slot="break1-eyebrow"]');
      var closeEl = el.querySelector('[data-slot="universal-close"]');

      var headline = band.headline;
      var bandBody = band.body.slice();
      if (headlineEl) headlineEl.textContent = headline.replace(/\[AGE\]/g, age);
      if (bodyEl) bodyEl.innerHTML = bandBody.map(function (p) { return '<p>' + escapeHtml(p).replace(/\[AGE\]/g, age) + '</p>'; }).join('');

      var pairKey = ensurePairKey();
      var bridge = C.BRIDGES[pairKey];
      if (eyebrowEl && pairKey) eyebrowEl.textContent = pairKey.split('_').map(function (key) { return C.GOAL_SHORT_LABEL[key] || key; }).join(' + ');
      if (bridgeHeadlineEl) bridgeHeadlineEl.textContent = bridge ? bridge[0] : '';
      if (bridgeEl) bridgeEl.innerHTML = bridge ? bridge.slice(1).map(function (p) { return '<p>' + escapeHtml(p) + '</p>'; }).join('') : '';
      if (closeEl) closeEl.innerHTML = C.UNIVERSAL_CLOSE.map(function (p) { return '<p>' + escapeHtml(p) + '</p>'; }).join('');

      var imageRoot = el.querySelector('.nad3-break__images');
      if (imageRoot) {
        var gender = state.answers.gender;
        var imageGender = gender === 'woman' ? 'F' : gender === 'man' ? 'M' : null;
        var imageBand = ageBandKey === 'under35' ? '1' : ageBandKey === 'mid' ? '2' : '3';
        var imageVariant = imageGender && pairKey && break1ImageMap[imageBand] && break1ImageMap[imageBand][imageGender]
          ? break1ImageMap[imageBand][imageGender][pairKey] : null;
        var personalizedMedia = imageRoot.querySelector('.nad3-break__media--personalized');
        var personalizedImage = personalizedMedia && personalizedMedia.querySelector('[data-break1-personalized]');
        var media = null;
        if (imageVariant && imageVariant.small && imageVariant.large && personalizedImage) {
          if (personalizedImage.getAttribute('src') !== imageVariant.large) {
            personalizedImage.srcset = imageVariant.small + ' 500w, ' + imageVariant.large + ' 1000w';
            personalizedImage.src = imageVariant.large;
          }
          media = personalizedMedia;
        } else if (imageGender) {
          media = imageRoot.querySelector(imageGender === 'F' ? '.nad3-break__media--woman' : '.nad3-break__media--man');
        }
        imageRoot.hidden = !media;
        var hero = imageRoot.closest('.nad3-break__hero');
        if (hero) hero.classList.toggle('nad3-break__hero--with-image', !!media);
        Array.prototype.forEach.call(imageRoot.querySelectorAll('.nad3-break__media'), function (node) { node.hidden = node !== media; });
      }
    }

    function renderAdaptive(stepKey, dataset) {
      var el = stepEls[stepKey];
      if (!el) return;
      var pairKey = ensurePairKey();
      var variant = dataset[pairKey];
      if (!variant) return;

      var qEl = el.querySelector('[data-slot="' + stepKey + '-question"]');
      if (qEl) qEl.textContent = variant.question;

      var container = el.querySelector('[data-options-root]');
      if (!container) return;

      // Q5/Q7 are unscored profiling data (Klaviyo/CRM only) — store the
      // human-readable label itself as the answer, not a positional key.
      var options = variant.options.map(function (opt) {
        if (typeof opt === 'string') return { key: opt, label: opt };
        return { key: opt.label, label: opt.label, exclusive: !!opt.exclusive };
      });
      renderOptions(container, options, { multi: true });

      var continueBtn = el.querySelector('[data-quiz-continue]');
      if (continueBtn) continueBtn.disabled = false; // Q5/Q7 are not scored; 0+ selections allowed
      bindMultiStep(stepKey, stepKey, { min: 0 });
    }

    function renderBreak2() {
      var el = stepEls.break2;
      if (!el) return;
      var prior = state.answers.prior;
      var variantKey = prior === 'none' ? 'variantB' : 'variantA';
      state.break2Variant = variantKey;
      var variant = C.BREAK2[variantKey];

      var headlineEl = el.querySelector('[data-slot="break2-headline"]');
      var bodyEl = el.querySelector('[data-slot="break2-body"]');
      var calloutHeadlineEl = el.querySelector('[data-slot="break2-callout-heading"]');
      var calloutBodyEl = el.querySelector('[data-slot="break2-callout-body"]');
      var eyebrowEl = el.querySelector('[data-slot="break2-eyebrow"]');
      if (headlineEl) headlineEl.textContent = variant.headline;
      if (eyebrowEl) eyebrowEl.textContent = variantKey === 'variantA' ? 'WHY IT DIDN’T WORK' : 'WHAT MOST PRODUCTS MISS';
      if (bodyEl) bodyEl.innerHTML = variant.body.slice(0, -3).map(function (p) { return '<p>' + escapeHtml(p) + '</p>'; }).join('');
      if (calloutHeadlineEl) calloutHeadlineEl.textContent = variant.body[variant.body.length - 3];
      if (calloutBodyEl) calloutBodyEl.innerHTML = variant.body.slice(-2).map(function (p) { return '<p>' + escapeHtml(p) + '</p>'; }).join('');
      var requestedImage = el.querySelector('[data-break2-image="' + variantKey + '"]');
      var visibleImage = requestedImage && requestedImage.querySelector('img')
        ? requestedImage
        : Array.prototype.find.call(el.querySelectorAll('[data-break2-image]'), function (image) { return !!image.querySelector('img'); });
      Array.prototype.forEach.call(el.querySelectorAll('[data-break2-image]'), function (image) {
        image.hidden = image !== visibleImage;
      });
    }

    function renderResult() {
      var el = stepEls.result;
      if (!el) return;
      var pairKey = ensurePairKey();
      var sku = state.sku = computeSku();
      var body = C.RESULTS[sku] && C.RESULTS[sku][pairKey];
      var age = state.answers.age || '';

      if (!pairKey || !body) return;

      Array.prototype.forEach.call(el.querySelectorAll('[data-result-sku]'), function (block) {
        var isMatch = block.getAttribute('data-result-sku') === sku;
        block.hidden = !isMatch;
        if (!isMatch) return;
        var bodyEl = block.querySelector('[data-slot="result-body"]');
        if (bodyEl && body) bodyEl.innerHTML = body.map(function (p) { return '<p>' + escapeHtml(p) + '</p>'; }).join('');
        var ageEl = block.querySelector('[data-slot="result-age"]');
        if (ageEl) ageEl.textContent = age;
      });

      if (!state.completedAt) {
        state.completedAt = new Date().toISOString();
        saveState();
        var offered = el.querySelector('[data-result-sku="' + sku + '"]');
        emit('complete', {
          quizName: root.getAttribute('data-quiz-name') || 'HPN NAD3 Quiz',
          pairKey: pairKey,
          sku: sku,
          ageBand: state.ageBand,
          break2Variant: state.break2Variant,
          offeredProductTitle: offered && offered.getAttribute('data-product-title'),
          offeredProductHandle: offered && offered.getAttribute('data-product-handle'),
          offeredVariantId: offered && offered.getAttribute('data-variant-id'),
          offeredPrice: offered && offered.getAttribute('data-price'),
          completedAt: state.completedAt,
          answers: Object.assign({}, state.answers)
        });
      }
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    // -----------------------------------------------------------------
    // ADD TO CART (single SKU, real Shopify product via schema picker)
    // -----------------------------------------------------------------
    function bindCartButtons() {
      var el = stepEls.result;
      if (!el) return;
      Array.prototype.forEach.call(el.querySelectorAll('[data-quiz-add-to-cart]'), function (btn) {
        btn.setAttribute('data-default-label', btn.textContent.trim());
        btn.addEventListener('click', function () {
          var variantId = btn.getAttribute('data-variant-id');
          if (!variantId) return;
          var statusEl = btn.parentElement && btn.parentElement.querySelector('[data-cart-status]');
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          var originalText = btn.textContent;
          btn.textContent = 'Adding…';
          if (statusEl) { statusEl.textContent = ''; statusEl.classList.remove('nad3-cart-status--error'); }

          var shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';

          fetch(shopRoot + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          })
            .then(function (response) {
              return response.json().catch(function () { return {}; }).then(function (payload) {
                if (!response.ok) throw new Error(payload.description || payload.message || 'This product could not be added to your cart.');
                return payload;
              });
            })
            .then(function () {
              emit('added-to-cart', { variantId: variantId, sku: state.sku });
              clearState();
              btn.removeAttribute('aria-busy');
              if (statusEl) statusEl.textContent = 'Added to your cart.';

              // The real Bundle Builder is a separate section rendered right
              // below this one on the same page template — scroll to it
              // instead of bouncing straight to /cart, if it's present.
              var bundleSection = document.getElementById('BundleBuilderPage');
              if (bundleSection) {
                btn.textContent = '✓ Added — see more below';
                setTimeout(function () {
                  bundleSection.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
                }, 400);
              } else {
                window.location.href = shopRoot + 'cart';
              }
            })
            .catch(function (error) {
              btn.disabled = false;
              btn.removeAttribute('aria-busy');
              btn.textContent = originalText;
              if (statusEl) {
                statusEl.textContent = error && error.message ? error.message : 'Something went wrong. Please try again.';
                statusEl.classList.add('nad3-cart-status--error');
              }
            });
        });
      });
    }

    function bindRetake() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-retake]'), function (btn) {
        btn.addEventListener('click', function () {
          clearState();
          state = createInitialState();
          Array.prototype.forEach.call(root.querySelectorAll('.nad3-choice-btn'), function (choice) {
            setSelected(choice, false);
          });
          Array.prototype.forEach.call(root.querySelectorAll('form'), function (form) { form.reset(); });
          Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-error]'), function (error) {
            error.hidden = true;
            error.textContent = '';
          });
          Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-add-to-cart]'), function (cartButton) {
            cartButton.disabled = false;
            cartButton.removeAttribute('aria-busy');
            cartButton.textContent = cartButton.getAttribute('data-default-label') || cartButton.textContent;
          });
          Array.prototype.forEach.call(root.querySelectorAll('[data-cart-status], [data-integration-status]'), function (status) {
            status.textContent = '';
          });
          syncContinueState('goals', { exact: 2 });
          ['gender', 'stimulants', 'prior', 'identity'].forEach(function (stepKey) {
            syncContinueState(stepKey, { exact: 1 });
          });
          showStep(0);
        });
      });
    }

    function bindBackNav() {
      var back = root.querySelector('[data-quiz-back]');
      if (back) back.addEventListener('click', goPrev);
    }

    function bindIntegrationStatus() {
      root.addEventListener('nad3-quiz:integration-error', function (event) {
        if (!event.detail || event.detail.integration !== 'klaviyo_sms') return;
        Array.prototype.forEach.call(root.querySelectorAll('[data-integration-status]'), function (status) {
          status.textContent = 'Your result is ready, but we could not save your text-message preference. Please try again later.';
        });
      });
    }

    function hydrateFromState() {
      restoreSelections('gender', 'gender');
      restoreSelections('goals', 'goals');
      restoreSelections('stimulants', 'stimulant');
      restoreSelections('prior', 'prior');
      restoreSelections('identity', 'identity');
      syncContinueState('goals', { exact: 2 });
      ['gender', 'stimulants', 'prior', 'identity'].forEach(function (stepKey) {
        syncContinueState(stepKey, { exact: 1 });
      });

      var ageInput = stepEls.age && stepEls.age.querySelector('[data-age-input]');
      if (ageInput && state.answers.age) ageInput.value = state.answers.age;

      if (ensurePairKey()) {
        renderAdaptive('q5', C.Q5);
        renderAdaptive('q7', C.Q7);
        restoreSelections('q5', 'q5');
        restoreSelections('q7', 'q7');
      }
    }

    // -----------------------------------------------------------------
    // INIT
    // -----------------------------------------------------------------
    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-next]'), function (btn) {
      // Steps whose only interaction is a plain "continue" button (no
      // choice/validation to gate on) — welcome and the two Break screens.
      var stepEl = btn.closest('[data-step]');
      if (stepEl && ['welcome', 'break1', 'break2'].indexOf(stepEl.getAttribute('data-step')) !== -1) {
        btn.addEventListener('click', function () {
          if (stepEl.getAttribute('data-step') === 'welcome') markStarted();
          goNext();
        });
      }
    });

    bindSingleStep('gender', 'gender');
    bindMultiStep('goals', 'goals', { exact: 2 });
    bindSingleStep('stimulants', 'stimulant');
    bindNumberStep();
    bindSingleStep('prior', 'prior');
    bindSingleStep('identity', 'identity');
    bindLeadStep();
    bindCartButtons();
    bindRetake();
    bindBackNav();
    bindIntegrationStatus();

    loadState();
    hydrateFromState();
    if (state.startedAt && !state.completedAt && currentStepKey() !== 'welcome') {
      emit('resumed', { startedAt: state.startedAt, step: currentStepKey() });
    }
    if (currentStepKey() === 'break1') renderBreak1();
    if (currentStepKey() === 'break2') renderBreak2();
    if (currentStepKey() === 'result') renderResult();
    showStep(state.stepIndex || 0);

    this.state = state;
    return this;
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = [];
    if (context.matches && context.matches('[data-nad3-quiz-root]')) roots.push(context);
    Array.prototype.push.apply(roots, context.querySelectorAll('[data-nad3-quiz-root]'));
    window.nad3QuizInstances = window.nad3QuizInstances || [];
    roots.forEach(function (root) {
      var instance = new Nad3Quiz(root);
      if (instance) window.nad3QuizInstances.push(instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) init(e.target);
  });

})();
